import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import CartDrawer from './CartDrawer';
import Toast from './Toast';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => { setMobileOpen(false); setCartOpen(false); }, [location.pathname]);

  const handleCartUpdate = useCallback(() => {
    setCartOpen(true);
    setToast('Added to cart');
  }, []);

  useEffect(() => {
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [handleCartUpdate]);

  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return <>{children}</>;

  const navItems = [
    { to: '/shop', label: 'Shop' },
    { to: '/about', label: 'About' },
    { to: '/faq', label: 'FAQ' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Announcement Bar */}
      <div className="ticker-bar">
        Free shipping on orders over $80 &nbsp;&mdash;&nbsp; Handcrafted in Phoenix, AZ
      </div>

      {/* Header */}
      <header className="site-header">
        <div className="header-inner">
          <Link to="/" className="site-logo">
            Frame My <span>Balls</span>
          </Link>

          <nav className="nav-links">
            {navItems.map(n => (
              <Link key={n.to} to={n.to} className={location.pathname === n.to ? 'active' : ''}>
                {n.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/account" className={location.pathname === '/account' ? 'active' : ''}>Account</Link>
                {user.role === 'admin' && <Link to="/admin">Admin</Link>}
                <button onClick={logout} aria-label="Logout">Logout</button>
              </>
            ) : (
              <Link to="/login">Login</Link>
            )}
            <button onClick={() => setCartOpen(true)} className="cart-badge" style={{ background: 'none', border: 'none', padding: 0 }} aria-label="Cart">
              <ShoppingCart size={20} />
              {cart.item_count > 0 && <span className="cart-count">{cart.item_count}</span>}
            </button>
          </nav>

          <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            &#9776;
          </button>
        </div>
      </header>

      {/* Mobile Nav */}
      <div className={`mobile-nav ${mobileOpen ? 'open' : ''}`}>
        <button className="mobile-nav-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">&times;</button>
        {navItems.map(n => (
          <Link key={n.to} to={n.to}>{n.label}</Link>
        ))}
        {user ? (
          <>
            <Link to="/account">Account</Link>
            {user.role === 'admin' && <Link to="/admin">Admin</Link>}
            <button onClick={() => { logout(); setMobileOpen(false); }}>Logout</button>
          </>
        ) : (
          <Link to="/login">Login</Link>
        )}
        <button onClick={() => { setMobileOpen(false); setCartOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShoppingCart size={24} /> Cart ({cart.item_count})
        </button>
      </div>

      {/* Cart Drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Toast */}
      {toast && <Toast message={toast} onClose={() => setToast('')} />}

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-grid">
          <div>
            <div className="footer-brand">Frame My <span>Balls</span></div>
            <p className="footer-tagline">
              Modular wall-mounted golf ball display racks. 3D-printed in Phoenix, Arizona.
            </p>
          </div>
          <div>
            <div className="footer-heading">Shop</div>
            <ul className="footer-links">
              <li><Link to="/shop">All Products</Link></li>
              <li><Link to="/shop/single-module">The Single</Link></li>
              <li><Link to="/shop/trophy-pair">The Trophy Pair</Link></li>
              <li><Link to="/shop/back-nine">The Back-Nine</Link></li>
            </ul>
          </div>
          <div>
            <div className="footer-heading">Company</div>
            <ul className="footer-links">
              <li><Link to="/about">About</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/shipping">Shipping</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="footer-heading">Account</div>
            <ul className="footer-links">
              {user ? (
                <>
                  <li><Link to="/account">My Account</Link></li>
                  <li><Link to="/order-lookup">Track Order</Link></li>
                </>
              ) : (
                <>
                  <li><Link to="/login">Sign In</Link></li>
                  <li><Link to="/register">Create Account</Link></li>
                  <li><Link to="/order-lookup">Track Order</Link></li>
                </>
              )}
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>&copy; {new Date().getFullYear()} Frame My Balls. All rights reserved.</span>
          <span>Phoenix, AZ</span>
        </div>
      </footer>
    </>
  );
}
